import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Routes } from '../../constants/RouteConstants';
import mainLogo from '../../Assets/images/main-logo.svg';
import mainLogoShort from '../../Assets/images/cms-logo.png';
import './landing.scss';
import { getFromStorage } from '../../utils/Helper';
import { StorageConstant } from '../../constants/Constants';
import LanguageDropdown from '../layout/LanguageDropdown';
import { LanguageConstants } from '../../constants/LanguageConstants';
import { ApiConstants } from '../../constants/ApiConstants';
import useMakeRequest from '../../utils/ApiHelper';
import { ensureBusinessProfileAllowsQuote, navigateOpenRequestQuoteModal } from '../../utils/quoteAccessGate';
import { ALL_REVIEWS, reviewInitials } from './landingReviewsData';
import Testimonial2 from './Testimonial2';
import Footer from './Footer';
import { useScrollToHash } from '../../utils/useScrollToHash';

type NavKey = 'what' | 'about' | 'trust';

export default function LandingHome() {
  const navigate = useNavigate();
  const profile = useSelector((state: any) => state?.ProfileState?.data?.data);
  const hasAccessToken = Boolean(getFromStorage({ key: StorageConstant?.ACCESS_TOKEN }));
  useScrollToHash();
  const navSections = [
    { id: 'landing-what-we-do', key: 'what' as const, label: LanguageConstants?.LANDING_NAV_WHAT },
    { id: 'landing-about', key: 'about' as const, label: LanguageConstants?.LANDING_NAV_ABOUT },
    { id: 'landing-trust', key: 'trust' as const, label: LanguageConstants?.LANDING_NAV_TRUST },
  ];
  const [navActive, setNavActive] = useState<NavKey>('what');
  const [navScrolled, setNavScrolled] = useState(false);
  const aboutCms = useMakeRequest();
  const [aboutCmsData, setAboutCmsData] = useState<any>(null);
  const lang = getFromStorage({ key: 'Translate' });

  const scrollToId = useCallback((id: string) => {
    if (id === 'landing-about') setNavActive('about');
    else if (id === 'landing-trust' || id === 'landing-reviews') setNavActive('trust');
    else setNavActive('what');
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /** Scrollspy: highlight menu item for section currently in view. */
  useEffect(() => {
    const sections: { id: string; key: NavKey }[] = [
      { id: 'landing-what-we-do', key: 'what' },
      { id: 'landing-how-it-works', key: 'what' },
      { id: 'landing-about', key: 'about' },
      { id: 'landing-trust', key: 'trust' },
      { id: 'landing-reviews', key: 'trust' },
    ];

    let rafId = 0;
    const headerActivationOffset = 108;

    const updateNavActive = () => {
      rafId = 0;
      const lineY = headerActivationOffset;
      let next: NavKey = 'what';
      let foundInView = false;

      for (const { id, key } of sections) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const isActiveByBounds = rect.top <= lineY && rect.bottom > lineY;
        if (isActiveByBounds) {
          next = key;
          foundInView = true;
        }
      }

      if (!foundInView) {
        // Fallback when line sits between sections: pick the last section above the line.
        let fallback: NavKey = 'what';
        for (const { id, key } of sections) {
          const el = document.getElementById(id);
          if (!el) continue;
          const top = el.getBoundingClientRect().top;
          if (top <= lineY) fallback = key;
        }
        next = fallback;
      }

      setNavActive((prev) => (prev === next ? prev : next));
    };

    const onScrollOrResize = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(updateNavActive);
    };

    updateNavActive();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    aboutCms.makeRequest({
      payload: {
        slug: 'about-us',
        user_type: 1,
        lang_id: lang === 'spa' ? '2' : '1',
      },
      url: ApiConstants?.Cms,
      toastStatus: false,
    });
  }, [lang]);

  useEffect(() => {
    if (aboutCms?.status) {
      setAboutCmsData(aboutCms?.data?.data);
    }
  }, [aboutCms?.status]);

  const aboutContentHtml = aboutCmsData?.cms_pages_details?.content;
  const hasCmsAboutBody = Boolean(String(aboutContentHtml ?? '').trim());

  return (
    <div className={`landing-page${hasAccessToken ? ' landing-page--app-header' : ''}`}>
      {!hasAccessToken ? (
      <header className={`landing-nav${navScrolled ? ' landing-nav--scrolled' : ''}`}>
        <nav className="navbar navbar-expand-lg navbar-light fixed-top">
          <div className="container landing-nav__inner">
            <Link className="landing-nav__logo navbar-brand" to="/">
              <img src={mainLogo} alt="" className="logo-large" width={160} height={40} />
              <img src={mainLogoShort} alt="" className="landing-nav__logo-img logo-short" width={160} height={40} />
            </Link>


            <div className="landing-nav__actions pb-2 pb-lg-0 pt-3 pt-lg-0 d-lg-none">
              <Link to={Routes.Login} className="landing-nav__login">
                {LanguageConstants?.LOGIN}
              </Link>
              <Link to={Routes.Signup} className="landing-nav__signup">
                {LanguageConstants?.SINGUP}
              </Link>
            </div>

            <button
              className="navbar-toggler landing-nav__mobile-toggle"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#landingNavbar"
              aria-controls="landingNavbar"
              aria-expanded="false"
              aria-label={LanguageConstants?.LANDING_ARIA_MENU}
            >
              <span className="navbar-toggler-icon" />
            </button>
            <div className="collapse navbar-collapse" id="landingNavbar">
              <ul className="navbar-nav landing-nav__links mx-auto pt-3 pt-lg-0" aria-label={LanguageConstants?.LANDING_NAV_PRIMARY}>
                {navSections.map((item) => (
                  <li className="nav-item" key={item.key}>
                    <button
                      type="button"
                      className={`landing-nav__link nav-link${navActive === item.key ? ' landing-nav__link--active active' : ''}`}
                      onClick={() => scrollToId(item.id)}
                      data-bs-toggle="collapse"
                      data-bs-target="#landingNavbar"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="landing-nav__actions pb-2 pb-lg-0 pt-3 pt-lg-0 d-none d-lg-flex">
              <Link to={Routes.Login} className="landing-nav__login">
                {LanguageConstants?.LOGIN}
              </Link>
              <Link to={Routes.Signup} className="landing-nav__signup">
                {LanguageConstants?.SINGUP}
              </Link>
            </div>

          </div>
        </nav>
      </header>
      ) : null}

      <section id="landing-what-we-do" className="landing-section landing-hero sec-bg">
        <div className="landing-hero__layout">
          <div className="landing-hero__center">
            <h1 className="landing-hero__title">
              {LanguageConstants?.LANDING_HERO_TITLE_1}
              <br />
              <span className="landing-hero__highlight">{LanguageConstants?.LANDING_HERO_TITLE_2}</span>
            </h1>
            <p className="landing-hero__sub">{LanguageConstants?.LANDING_HERO_SUB}</p>
            <div className="landing-hero__ctas">
              <div className="quote-animation-wrap">
                <button
                  type="button"
                  className="round-btn quote-animation"
                  onClick={() => {
                    if (!hasAccessToken) {
                      navigate(Routes.Login);
                      return;
                    }
                    if (!ensureBusinessProfileAllowsQuote(profile, navigate, LanguageConstants as any)) {
                      return;
                    }
                    navigateOpenRequestQuoteModal(navigate);
                  }}
                >
                  <span className="word-first">{LanguageConstants?.REQUESTQUOTES}</span>
                  <span className="word-second">{LanguageConstants?.LETS_GO}</span>
                </button>
              </div>
              <button
                type="button"
                className="round-btn round-btn-outline"
                onClick={() => scrollToId('landing-how-it-works')}
              >
                {LanguageConstants?.LANDING_HOW_BTN}
              </button>
            </div>
          </div>
        </div>
        <img className="landing-hero__visual"
          src={`${process.env.PUBLIC_URL}/landing-page/${encodeURIComponent('hand-mobile.png')}`}
          alt={LanguageConstants?.LANDING_HERO_ALT}
        />
      </section>

      <section id="landing-how-it-works" className="landing-section landing-how sec-y-space">
        <div className='container'>
          <div className="landing-how__inner">
            <h2 className="landing-title text-center">{LanguageConstants?.LANDING_HOW_TITLE}</h2>
            <div className="landing-how__steps">
              <div className="landing-how__step">
                <div className="landing-how__circle">1</div>
                <h3 className="landing-how__step-title">{LanguageConstants?.LANDING_STEP1_TITLE}</h3>
                <p className="landing-how__step-desc">{LanguageConstants?.LANDING_STEP1_DESC}</p>
              </div>
              <div className="landing-how__step">
                <div className="landing-how__circle">2</div>
                <h3 className="landing-how__step-title">{LanguageConstants?.LANDING_STEP2_TITLE}</h3>
                <p className="landing-how__step-desc">{LanguageConstants?.LANDING_STEP2_DESC}</p>
              </div>
              <div className="landing-how__step">
                <div className="landing-how__circle">3</div>
                <h3 className="landing-how__step-title">{LanguageConstants?.LANDING_STEP3_TITLE}</h3>
                <p className="landing-how__step-desc">{LanguageConstants?.LANDING_STEP3_DESC}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="landing-about" className="landing-section landing-about sec-y-space sec-bg">
        <div className="container">
          <div className="landing-about__grid">
            <div className="pe-lg-4 pe-xl-5">
              <span className="sec-ttl mb-4 mb-xxl-5 text-capitalize">{LanguageConstants?.LANDING_ABOUT_LABEL}</span>
              <h2 className="landing-title">{LanguageConstants?.LANDING_ABOUT_HEADING}</h2>
            </div>
            <div className="landing-about__body">
              {hasCmsAboutBody ? (
                <div dangerouslySetInnerHTML={{ __html: aboutContentHtml }} />
              ) : (
                <>
                  <p>{LanguageConstants?.LANDING_ABOUT_P1}</p>
                  <p>{LanguageConstants?.LANDING_ABOUT_P2}</p>
                  <p>{LanguageConstants?.LANDING_ABOUT_P3}</p>
                  <p>{LanguageConstants?.LANDING_ABOUT_P4}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="landing-trust" className="landing-section landing-trust sec-y-space">
        <div className="container">
          <p className="sec-ttl mb-4 text-capitalize">{LanguageConstants?.LANDING_TRUST_LABEL}</p>
          <div className="row gy-4">
            <div className='col-sm-6 col-xl-3'>
              <div className="landing-trust__card">
                <div className="landing-trust__icon-wrap">
                  <img src={`${process.env.PUBLIC_URL}/landing-page/${encodeURIComponent('trust-1.png')}`} alt={LanguageConstants?.LANDING_TRUST_CARD1_TITLE} />
                </div>
                <h3 className="landing-trust__card-title">{LanguageConstants?.LANDING_TRUST_CARD1_TITLE}</h3>
                <p className="landing-trust__card-desc">{LanguageConstants?.LANDING_TRUST_CARD1_DESC}</p>
              </div>
            </div>
            <div className='col-sm-6 col-xl-3'>
              <div className="landing-trust__card">
                <div className="landing-trust__icon-wrap">
                  <img src={`${process.env.PUBLIC_URL}/landing-page/${encodeURIComponent('trust-2.png')}`} alt={LanguageConstants?.LANDING_TRUST_CARD2_TITLE} />
                </div>
                <h3 className="landing-trust__card-title">{LanguageConstants?.LANDING_TRUST_CARD2_TITLE}</h3>
                <p className="landing-trust__card-desc">{LanguageConstants?.LANDING_TRUST_CARD2_DESC}</p>
              </div>
            </div>
            <div className='col-sm-6 col-xl-3'>
              <div className="landing-trust__card">
                <div className="landing-trust__icon-wrap">
                  <img src={`${process.env.PUBLIC_URL}/landing-page/${encodeURIComponent('trust-3.png')}`} alt={LanguageConstants?.LANDING_TRUST_CARD3_TITLE} />
                </div>
                <h3 className="landing-trust__card-title">{LanguageConstants?.LANDING_TRUST_CARD3_TITLE}</h3>
                <p className="landing-trust__card-desc">{LanguageConstants?.LANDING_TRUST_CARD3_DESC}</p>
              </div>
            </div>
            <div className='col-sm-6 col-xl-3'>
              <div className="landing-trust__card">
                <div className="landing-trust__icon-wrap">
                  <img src={`${process.env.PUBLIC_URL}/landing-page/${encodeURIComponent('trust-4.png')}`} alt={LanguageConstants?.LANDING_TRUST_CARD4_TITLE} />
                </div>
                <h3 className="landing-trust__card-title">{LanguageConstants?.LANDING_TRUST_CARD4_TITLE}</h3>
                <p className="landing-trust__card-desc">{LanguageConstants?.LANDING_TRUST_CARD4_DESC}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Testimonial2 />

      <Footer />
      
    </div>
  );
}
